import React, { FC, useEffect, useMemo, useState } from 'react'
import { EmptySection, HeadSection, RequestCard } from 'src/components/elements'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { useGetPropertyRequestsQuery, useGetPropertyTabsQuery } from 'src/redux/api'
import { MyPagination } from 'src/components/ui'
import SectionHint from 'src/components/elements/section-hint/SectionHint'
import { Skeleton } from '@mui/material'
import { skipToken } from '@reduxjs/toolkit/query'
import ErrorState from 'src/components/elements/error-state/ErrorState'

interface IRequestsList {
  // On the unified home page an empty section disappears instead of pushing
  // its own CTA — the hero already carries it (38.2)
  hideWhenEmpty?: boolean
  // Rendered inside the unified RequestsInbox (D2/D3): the inbox owns the
  // heading, so hide the section's own header/hint.
  embedded?: boolean
}

type TabPropertiesItem = { id: string; label: string; count?: number }

const RequestsList: FC<IRequestsList> = ({ hideWhenEmpty, embedded }) => {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<TabPropertiesItem | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [respondedView, setRespondedView] = useState(false)
  const {
    data: tabsData,
    isLoading: tabsLoading,
    isError: tabsFailed,
    refetch: refetchTabs,
  } = useGetPropertyTabsQuery(undefined)

  // Create tabs array with "All" tab at the beginning
  const tabsWithAll = useMemo(() => {
    if (!tabsData?.tabs || tabsData.tabs.length === 0) return []

    return [{ id: 'all', label: t('all'), count: tabsData.totalCount }, ...tabsData.tabs]
  }, [tabsData, t])

  const tabs = tabsData?.tabs || []

  const {
    data,
    isLoading,
    isError: requestsFailed,
    refetch: refetchRequests,
  } = useGetPropertyRequestsQuery(
    activeTab
      ? { propertyId: activeTab?.id, currentPage, limit: 8, responded: respondedView || undefined }
      : skipToken,
    { skip: !activeTab, refetchOnMountOrArgChange: true },
  )
  const properties = data?.items || []
  const totalPages = data?.totalPages || 1

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  useEffect(() => {
    if (tabsWithAll && tabsWithAll.length > 0 && !activeTab) {
      setActiveTab(tabsWithAll[0])
    }
  }, [tabsWithAll, activeTab])

  // Reset page to 1 when switching tabs
  useEffect(() => {
    if (activeTab) {
      setCurrentPage(1)
    }
  }, [activeTab?.id])

  if (hideWhenEmpty && !tabsLoading && !tabsFailed && tabs.length === 0) {
    return null
  }

  return (
    <section className="flex flex-col gap-4">
      {!embedded && (
        <>
          <HeadSection title={t('requests')} />
          <SectionHint storageKey="requests-section" text={t('hints.requests_section')} />
        </>
      )}
      {isLoading || tabsLoading ? (
        <Skeleton variant="rounded" height={400} className="w-full h-[50rem]" />
      ) : tabsFailed ? (
        // a failed load used to read as "you have no listings yet"
        <ErrorState onRetry={refetchTabs} />
      ) : tabs.length === 0 ? (
        <EmptySection
          title={t('emptyRequestSection.title')}
          description={t('emptyRequestSection.description')}
          buttonText={t('emptyRequestSection.buttonText')}
          buttonLink={'/create-ad'}
        />
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col">
            <nav className="flex overflow-x-auto  md:m-0 md:p-0 -mx-4 px-4 pr-4">
              {tabsWithAll?.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab)}
                  className={clsx(
                    'shrink-0 h-11 px-4 text-center min-w-[6.4375rem] relative inline-flex gap-4 text-base items-center justify-center transition-all duration-300',
                    activeTab?.id === tab.id
                      ? 'text-brand-ink font-extrabold'
                      : 'text-ink font-bold hover:text-brand-ink',
                  )}
                >
                  <span>{tab.label}</span>
                  {tab.count !== undefined && (
                    <span
                      className={clsx(
                        'inline-flex items-center justify-center text-center p-1 min-w-6 h-6 text-xs font-bold text-ink rounded-full border border-solid border-ink/[0.18] transition-all duration-300',
                        activeTab?.id === tab.id &&
                          '!border-brand-strong bg-brand-strong text-white',
                      )}
                    >
                      {tab.count}
                    </span>
                  )}
                  <span
                    className={`h-0.5 bg-brand absolute bottom-0 left-1/2 -translate-x-1/2 z-10 pointer-events-none transition-all duration-300 ${
                      activeTab?.id === tab.id ? 'w-full' : 'w-0'
                    }`}
                  />
                </button>
              ))}
            </nav>
            <hr className="shrink-0 self-stretch h-px m-0 border-0 bg-ink/[0.12] w-full -mt-px" />
          </div>
          <div className="flex gap-2">
            {[
              { value: false, label: t('request.new_requests') },
              { value: true, label: t('request.responded_requests') },
            ].map((option) => (
              <button
                key={String(option.value)}
                type="button"
                aria-pressed={respondedView === option.value}
                onClick={() => {
                  setRespondedView(option.value)
                  setCurrentPage(1)
                }}
                className={
                  respondedView === option.value
                    ? 'px-3.5 min-h-10 max-md:min-h-11 text-sm rounded-full border border-solid border-brand-strong bg-brand-soft text-brand-ink font-extrabold transition-colors duration-150'
                    : 'px-3.5 min-h-10 max-md:min-h-11 text-sm rounded-full border border-solid border-ink/[0.18] bg-surface text-ink font-bold hover:border-brand transition-colors duration-150'
                }
              >
                {option.label}
              </button>
            ))}
          </div>
          <div className="grid md:grid-cols-5 xl:grid-cols-6 gap-4">
            {properties.length > 0 &&
              properties.map((request, index) => (
                <RequestCard
                  key={`${request.propertyId}-${request.id}-${index}`}
                  request={request}
                  responded={respondedView}
                />
              ))}
          </div>
          {requestsFailed && <ErrorState onRetry={refetchRequests} />}
          {!requestsFailed && properties.length === 0 && (
            <EmptySection
              title={t('emptyRequestTabSection.title')}
              description=""
              buttonLink={'/create-ad'}
            />
          )}
          <MyPagination
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </section>
  )
}

export default RequestsList
